import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { LiveWineView } from '@gwc/shared';
import type { Registration } from '../api/tastings';
import { revealApi } from '../api/reveal';

/**
 * Step one of the reveal: whose bottle is under this letter, then **Reveal
 * wine**. The room sees the label and the details; who brought it waits for
 * step two. A letter bagged ahead of time arrives pre-selected, and attendees
 * whose wine already sits on another letter are left out of the list rather
 * than letting the host pick them and hit a conflict.
 */
export function UnveilRegistration({
  eventId,
  wine,
  registrations,
  linkedElsewhere,
  enabled,
  onChange,
}: {
  eventId: string;
  wine: LiveWineView;
  /** The tasting's registrations (admin list). */
  registrations: Registration[];
  /** Registration ids already on another letter. */
  linkedElsewhere: Set<string>;
  /** The reveal has started. */
  enabled: boolean;
  onChange: () => void;
}) {
  const [selected, setSelected] = useState(wine.registrationId ?? '');
  const [touched, setTouched] = useState(false);
  // Follow an assignment made elsewhere (the dashboard, another admin) until the
  // host picks something here.
  useEffect(() => {
    if (!touched) setSelected(wine.registrationId ?? '');
  }, [wine.registrationId, touched]);

  const choices = registrations.filter(
    (r) => r.status === 'registered' && !linkedElsewhere.has(r.id),
  );
  const row = choices.find((r) => r.id === selected);
  // The slot may already know what it is without a registration: a Cellar link,
  // or details typed in by hand.
  const identified = !!(wine.registrationId || wine.catalogId || wine.wineName || wine.producer);

  const reveal = useMutation({
    mutationFn: () =>
      revealApi.revealWine(
        eventId,
        wine.id,
        selected && selected !== wine.registrationId ? selected : undefined,
      ),
    onSuccess: () => {
      setTouched(false);
      onChange();
    },
  });

  return (
    <div className="space-y-2">
      <label className="block text-sm text-cream-200">
        Whose wine is this?
        <select
          className="field mt-1"
          value={selected}
          onChange={(e) => {
            setTouched(true);
            setSelected(e.target.value);
          }}
        >
          <option value="">
            {wine.registrationId || !identified
              ? 'Choose a confirmed attendee…'
              : 'As entered below'}
          </option>
          {choices.map((r) => (
            <option key={r.id} value={r.id}>
              {r.firstName} {r.lastName} — {r.wineName || 'No wine entered'}
              {r.id === wine.registrationId ? ' (bagged under this letter)' : ''}
            </option>
          ))}
        </select>
      </label>
      {registrations.some((r) => r.status === 'registered') && choices.length === 0 && (
        <p className="text-xs text-cream-300/60">
          Every confirmed attendee’s wine is already on another letter.
        </p>
      )}
      {row?.labelImageUrl && !wine.revealed && (
        <img
          className="h-24 rounded object-contain"
          src={row.labelImageUrl}
          alt={`${row.firstName} ${row.lastName}’s wine label`}
        />
      )}
      <button
        type="button"
        className="btn bg-wine-700 px-3 py-1.5 text-xs text-on-wine hover:bg-wine-500"
        disabled={!enabled || !(selected || identified) || reveal.isPending}
        onClick={() => reveal.mutate()}
        title={enabled ? undefined : 'Start the reveal first'}
      >
        Reveal wine
      </button>
      {!enabled && (
        <p className="text-xs text-cream-300/60">Available once the reveal has started.</p>
      )}
      {reveal.error && (
        <p role="alert" className="text-xs text-danger">
          {reveal.error.message}
        </p>
      )}
    </div>
  );
}
